import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";

import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import EmployeesPage from "@/pages/EmployeesPage";
import ShiftsPage from "@/pages/ShiftsPage";
import PerformancePage from "@/pages/PerformancePage";
import BrigadePage from "@/pages/BrigadePage";
import SettingsPage from "@/pages/SettingsPage";
import StatsPage from "@/pages/StatsPage";
import PortalLoginPage from "@/pages/PortalLoginPage";
import PortalDashboardPage from "@/pages/PortalDashboardPage";
import PricingPage from "@/pages/PricingPage";
import SubscriptionResultPage from "@/pages/SubscriptionResultPage";
import InstructionsPage from "@/pages/InstructionsPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import AuthCallback from "@/pages/AuthCallback";
import SaLoginPage from "@/pages/SaLoginPage";
import SaCmsPage from "@/pages/SaCmsPage";
import BillingInfoPage from "@/pages/BillingInfoPage";
import TrialExpiredPage from "@/pages/TrialExpiredPage";
import { useAuth } from "@/contexts/AuthContext";

// Lightweight gate: requires logged-in user (no trial check; billing form
// is accessible even when trial is over so the user can complete payment).
function RequireUser({ children }) {
    const { user, loading } = useAuth();
    if (loading || user === null)
        return (
            <div className="min-h-screen flex items-center justify-center bg-sf-bg">
                <div className="font-display text-2xl text-navy animate-pulse">
                    Caricamento…
                </div>
            </div>
        );
    if (!user) return <Navigate to="/login" replace />;
    return children;
}

// Intercept Emergent Google OAuth callback (#session_id=...) BEFORE normal routing.
function AppRouter() {
    const location = useLocation();
    if (location.hash?.includes("session_id=")) {
        return <AuthCallback />;
    }
    return (
        <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/istruzioni" element={<InstructionsPage />} />
            <Route path="/billing/success" element={<SubscriptionResultPage />} />
            <Route path="/sa-login" element={<SaLoginPage />} />
            <Route path="/portal-login" element={<PortalLoginPage />} />
            <Route path="/portal" element={<PortalDashboardPage />} />
            <Route path="/sa/cms" element={<SaCmsPage />} />
            <Route path="/trial-expired" element={<TrialExpiredPage />} />
            <Route
                path="/billing/info"
                element={
                    <RequireUser>
                        <BillingInfoPage />
                    </RequireUser>
                }
            />
            <Route
                path="/app"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <DashboardPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/app/dipendenti"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <EmployeesPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/app/turni"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <ShiftsPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/app/performance"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <PerformancePage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/app/brigata"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <BrigadePage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/app/statistiche"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <StatsPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route
                path="/app/impostazioni"
                element={
                    <ProtectedRoute>
                        <Layout>
                            <SettingsPage />
                        </Layout>
                    </ProtectedRoute>
                }
            />
            <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
    );
}

function App() {
    return (
        <AuthProvider>
            <BrowserRouter>
                <Toaster richColors position="top-right" />
                <AppRouter />
            </BrowserRouter>
        </AuthProvider>
    );
}

export default App;
